import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import CareIcon from "@/CAREUI/icons/CareIcon";
import { TableSkeleton } from "@/components/Common/SkeletonLoading";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  ACCOUNT_BILLING_STATUS_COLORS,
  ACCOUNT_STATUS_COLORS,
  AccountBase,
  AccountRead,
  AccountStatus,
} from "@/types/billing/account/Account";
import accountApi from "@/types/billing/account/accountApi";
import paymentReconciliationApi from "@/types/billing/paymentReconciliation/paymentReconciliationApi";
import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";

interface TransferPaymentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  facilityId: string;
  account: AccountRead;
}

interface PaymentTransferError {
  detail?: string;
  errors?: Array<{ msg?: string; error?: string }>;
  non_field_errors?: string[];
}

export default function TransferPaymentSheet({
  open,
  onOpenChange,
  facilityId,
  account,
}: TransferPaymentSheetProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  );
  const [amount, setAmount] = useState("");

  const { data: accountsResponse, isLoading } = useQuery({
    queryKey: ["accounts", account.patient.id, facilityId, "transfer-payment"],
    queryFn: query(accountApi.listAccount, {
      pathParams: { facilityId },
      queryParams: {
        patient: account.patient.id,
        status: AccountStatus.active,
        limit: 50,
      },
    }),
    enabled: open,
  });

  const accounts = (
    accountsResponse?.results as AccountBase[] | undefined
  )?.filter((candidate) => candidate.id !== account.id);

  const reset = () => {
    setSelectedAccountId(null);
    setAmount("");
  };

  const { mutate: submitTransfer, isPending } = useMutation({
    mutationFn: mutate(paymentReconciliationApi.transferPayment, {
      pathParams: { facilityId },
      silent: true,
    }),
    onSuccess: () => {
      toast.success(t("payment_transferred_successfully"));
      queryClient.invalidateQueries({ queryKey: ["account", account.id] });
      queryClient.invalidateQueries({ queryKey: ["payments", account.id] });
      queryClient.invalidateQueries({
        queryKey: ["account", selectedAccountId],
      });
      queryClient.invalidateQueries({
        queryKey: ["payments", selectedAccountId],
      });
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      const cause = (error as Error & { cause?: PaymentTransferError }).cause;

      if (cause?.detail) {
        toast.error(cause.detail);
      } else if (cause?.errors?.[0]) {
        toast.error(
          cause.errors[0].msg ||
            cause.errors[0].error ||
            t("payment_transfer_failed"),
        );
      } else if (cause?.non_field_errors?.[0]) {
        toast.error(cause.non_field_errors[0]);
      } else {
        toast.error(t("payment_transfer_failed"));
      }
    },
  });

  const handleSubmit = () => {
    const parsedAmount = Number(amount);
    const totalPaid = Number(account.total_paid || "0");
    if (
      !selectedAccountId ||
      !Number.isFinite(parsedAmount) ||
      parsedAmount <= 0
    ) {
      return;
    }
    if (parsedAmount > totalPaid) {
      toast.error(t("transfer_amount_exceeds_total_paid"));
      return;
    }

    submitTransfer({
      source_account: account.id,
      target_account: selectedAccountId,
      amount,
    });
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) reset();
      }}
    >
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t("transfer_payment")}</SheetTitle>
          <SheetDescription>
            {t("transfer_payment_description")}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="transfer-amount">{t("amount")}</Label>
            <Input
              id="transfer-amount"
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              placeholder={t("enter_amount")}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">
              {t("select_target_account")}
            </p>
            {isLoading ? (
              <TableSkeleton count={3} />
            ) : !accounts?.length ? (
              <EmptyState
                icon={
                  <CareIcon icon="l-user" className="size-6 text-primary" />
                }
                title={t("no_other_accounts_available")}
              />
            ) : (
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {accounts.map((candidate) => (
                  <button
                    key={candidate.id}
                    type="button"
                    className={cn(
                      "w-full rounded-md border p-3 text-left transition-colors hover:bg-gray-50",
                      selectedAccountId === candidate.id
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : "border-gray-200",
                    )}
                    onClick={() => setSelectedAccountId(candidate.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {candidate.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <Badge
                          variant={ACCOUNT_STATUS_COLORS[candidate.status]}
                        >
                          {t(candidate.status)}
                        </Badge>
                        <Badge
                          variant={
                            ACCOUNT_BILLING_STATUS_COLORS[
                              candidate.billing_status
                            ]
                          }
                        >
                          {t(candidate.billing_status)}
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <SheetFooter className="pt-2">
          <SheetClose asChild>
            <Button variant="outline" type="button">
              {t("cancel")}
            </Button>
          </SheetClose>
          <Button
            onClick={handleSubmit}
            disabled={
              !selectedAccountId || !amount || Number(amount) <= 0 || isPending
            }
          >
            {isPending ? (
              <>
                <CareIcon
                  icon="l-spinner"
                  className="mr-2 size-4 animate-spin"
                />
                {t("transferring")}
              </>
            ) : (
              t("transfer_payment")
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
